import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { SB } from './sidebarConfig';

// Pure inline-style sidebar matching the PDF reference.
// Rules: height: ${height}px (never auto), ref on INNER div, useEffect has NO dependency array.

export function SidebarNavItem({ item, collapsed, t, hasAccess, setMobileOpen, msgUnread, lowStockCount, autopilotCount, ordersPendingCount, openMenuId, setOpenMenuId, location }) {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);
  const [hoveredChild, setHoveredChild] = useState(null);
  const [showTooltip, setShowTooltip] = useState(false);
  const bodyRef = useRef(null);
  const [height, setHeight] = useState(0);

  const fullLocation = location.pathname + location.hash;
  const hasChildren = item.children && item.children.length > 0;
  const visibleChildren = hasChildren ? item.children.filter(c => c.permKey === null || hasAccess(c.permKey)) : [];
  const isChildActive = visibleChildren.some(c => fullLocation === c.path || location.pathname === c.path);
  const isHashPath = item.path.includes('#');
  const isActive = isHashPath ? fullLocation === item.path : (location.pathname === item.path || isChildActive);
  const isOpen = openMenuId === item.path;

  // height: runs every render, NO dependency array
  useEffect(() => {
    if (!bodyRef.current) return;
    setHeight(isOpen ? bodyRef.current.scrollHeight : 0);
  });

  /* parent with children */
  if (hasChildren && visibleChildren.length > 0) {
    return (
      <div style={{ marginBottom: 2 }}>
        {/* tooltip wrapper */}
        <div style={{ position: 'relative' }}
          onMouseEnter={() => { setHovered(true); if (collapsed) setShowTooltip(true); }}
          onMouseLeave={() => { setHovered(false); setShowTooltip(false); }}
        >
          {/* parent button */}
          <button
            onClick={() => {
              setOpenMenuId(prev => prev === item.path ? null : item.path);
            }}
            data-testid={`nav-${item.labelKey}`}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: collapsed ? '10px 0' : '9px 12px',
              justifyContent: collapsed ? 'center' : 'flex-start',
              borderRadius: SB.radius,
              border: 'none',
              cursor: 'pointer',
              fontSize: 13.5,
              fontWeight: isActive ? 600 : 500,
              fontFamily: 'inherit',
              color: isActive ? SB.primary : hovered ? SB.fg : SB.muted,
              background: isActive ? SB.primaryBg : hovered ? SB.hoverBg : SB.bg,
              transition: `background ${SB.transition}, color ${SB.transition}`,
              textAlign: 'left',
              position: 'relative',
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'manipulation',
              userSelect: 'none',
            }}
          >
            <span style={{ flexShrink: 0, display: 'flex', position: 'relative', color: isActive ? SB.primary : hovered ? SB.fg : SB.mutedSoft, transition: `color ${SB.transition}` }}>
              {item.icon && <item.icon style={{ width: 17, height: 17 }} />}
              {/* Badge on operations parent icon when collapsed */}
              {collapsed && lowStockCount > 0 && item.children?.some(c => c.path === '/low-stock') && (
                <span style={{
                  position: 'absolute', top: -6, right: -6, minWidth: 14, height: 14,
                  fontSize: 9, fontWeight: 700, background: '#f97316', color: '#fff',
                  borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 2px',
                }} data-testid="low-stock-badge-icon">
                  {lowStockCount > 99 ? '99+' : lowStockCount}
                </span>
              )}
            </span>
            {!collapsed && (
              <>
                <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {t(item.labelKey)}
                </span>
                {/* Badge count next to label on expanded */}
                {lowStockCount > 0 && item.children?.some(c => c.path === '/low-stock') && (
                  <span style={{
                    minWidth: 18, height: 18, fontSize: 10, fontWeight: 700,
                    background: '#f97316', color: '#fff', borderRadius: 9,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '0 4px', marginRight: 4, flexShrink: 0,
                  }} data-testid="low-stock-badge-label">
                    {lowStockCount > 99 ? '99+' : lowStockCount}
                  </span>
                )}
                <ChevronDown style={{
                  width: 14, height: 14, flexShrink: 0,
                  transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                  transition: 'transform 0.2s ease',
                  opacity: isActive ? 0.6 : 0.4,
                }} />
              </>
            )}
          </button>

          {/* collapsed tooltip */}
          {collapsed && showTooltip && (
            <div style={{
              position: 'absolute', left: 'calc(100% + 10px)', top: '50%', transform: 'translateY(-50%)',
              zIndex: 999, background: SB.tooltipBg, color: SB.tooltipFg,
              fontSize: 12, fontWeight: 600, padding: '6px 10px', borderRadius: 8,
              whiteSpace: 'nowrap', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', pointerEvents: 'none',
            }}>
              {t(item.labelKey)}
            </div>
          )}
        </div>

        {/* OUTER div — animated height */}
        {!collapsed && (
          <div style={{
            height: `${height}px`,
            overflow: 'hidden',
            transition: 'height 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
            willChange: 'height',
          }}>
            {/* INNER div — ref for measurement */}
            <div ref={bodyRef} style={{ paddingTop: 2, paddingBottom: 4 }}>
              {visibleChildren.map((child) => {
                const childIsHash = child.path.includes('#');
                const childActive = childIsHash ? fullLocation === child.path : location.pathname === child.path;
                const childHov = hoveredChild === child.path;

                return (
                  <NavLink
                    key={child.path}
                    to={child.path}
                    onClick={(e) => { e.stopPropagation(); setMobileOpen(false); }}
                    onMouseEnter={() => setHoveredChild(child.path)}
                    onMouseLeave={() => setHoveredChild(null)}
                    data-testid={`nav-${child.path.slice(1)}`}
                    className={() => ''}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '7px 12px',
                      paddingLeft: 42,
                      borderRadius: 6,
                      fontSize: 13,
                      fontWeight: childActive ? 600 : 500,
                      textDecoration: 'none',
                      color: childActive ? SB.primary : childHov ? SB.fg : SB.muted,
                      background: childActive ? SB.primaryBg : childHov ? SB.hoverBg : 'transparent',
                      borderLeft: childActive ? `2px solid ${SB.primary}` : '2px solid transparent',
                      transition: `background ${SB.transition}, color ${SB.transition}`,
                    }}
                  >
                    <span style={{ flex: 1 }}>{t(child.labelKey)}</span>
                    {child.path === '/orders' && ordersPendingCount > 0 && (
                      <span style={{
                        minWidth: 16, height: 16, fontSize: 9, fontWeight: 700,
                        background: '#1f9d6c', color: '#fff', borderRadius: 8,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        padding: '0 3px', flexShrink: 0,
                      }} data-testid="orders-badge-child">
                        {ordersPendingCount > 99 ? '99+' : ordersPendingCount}
                      </span>
                    )}
                    {child.path === '/low-stock' && lowStockCount > 0 && (
                      <span style={{
                        minWidth: 16, height: 16, fontSize: 9, fontWeight: 700,
                        background: '#f97316', color: '#fff', borderRadius: 8,
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        padding: '0 3px', flexShrink: 0,
                      }} data-testid="low-stock-badge-child">
                        {lowStockCount > 99 ? '99+' : lowStockCount}
                      </span>
                    )}
                  </NavLink>
                );
              })}
            </div>
          </div>
        )}
      </div>
    );
  }

  /* leaf item (no children) */
  return (
    <div style={{ position: 'relative', marginBottom: 2 }}
      onMouseEnter={() => { setHovered(true); if (collapsed) setShowTooltip(true); }}
      onMouseLeave={() => { setHovered(false); setShowTooltip(false); }}
    >
      <NavLink
        to={item.path}
        onClick={(e) => { e.stopPropagation(); setMobileOpen(false); }}
        data-testid={`nav-${item.labelKey || item.path.slice(1)}`}
        className={() => ''}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: collapsed ? '10px 0' : '9px 12px',
          justifyContent: collapsed ? 'center' : 'flex-start',
          borderRadius: SB.radius,
          textDecoration: 'none',
          fontSize: 13.5,
          fontWeight: isActive ? 600 : 500,
          color: isActive ? SB.primary : hovered ? SB.fg : SB.muted,
          background: isActive ? SB.primaryBg : hovered ? SB.hoverBg : 'transparent',
          transition: `background ${SB.transition}, color ${SB.transition}`,
        }}
      >
        <span style={{ position: 'relative', flexShrink: 0, display: 'flex', color: isActive ? SB.primary : hovered ? SB.fg : SB.mutedSoft, transition: `color ${SB.transition}` }}>
          {item.icon && <item.icon style={{ width: 17, height: 17 }} />}
          {item.path === '/messages' && msgUnread > 0 && (
            <span style={{
              position: 'absolute', top: -6, right: -6, minWidth: 14, height: 14,
              fontSize: 9, fontWeight: 700, background: '#ef4444', color: '#fff',
              borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 2px',
            }}>
              {msgUnread > 99 ? '99+' : msgUnread}
            </span>
          )}
          {item.path === '/autopilot' && autopilotCount > 0 && collapsed && (
            <span style={{
              position: 'absolute', top: -6, right: -6, minWidth: 14, height: 14,
              fontSize: 9, fontWeight: 700, background: '#3b82f6', color: '#fff',
              borderRadius: 7, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 2px',
            }} data-testid="autopilot-badge-icon">
              {autopilotCount > 99 ? '99+' : autopilotCount}
            </span>
          )}
        </span>
        {!collapsed && (
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {t(item.labelKey)}
          </span>
        )}
        {!collapsed && item.path === '/autopilot' && autopilotCount > 0 && (
          <span style={{
            minWidth: 18, height: 18, fontSize: 10, fontWeight: 700,
            background: '#3b82f6', color: '#fff', borderRadius: 9,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 4px', flexShrink: 0,
          }} data-testid="autopilot-badge-label">
            {autopilotCount > 99 ? '99+' : autopilotCount}
          </span>
        )}
      </NavLink>

      {/* collapsed tooltip */}
      {collapsed && showTooltip && (
        <div style={{
          position: 'absolute', left: 'calc(100% + 10px)', top: '50%', transform: 'translateY(-50%)',
          zIndex: 999, background: SB.tooltipBg, color: SB.tooltipFg,
          fontSize: 12, fontWeight: 600, padding: '6px 10px', borderRadius: 8,
          whiteSpace: 'nowrap', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', pointerEvents: 'none',
        }}>
          {t(item.labelKey)}
        </div>
      )}
    </div>
  );
}
